import React, { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  ButtonGroup,
  Tooltip as MuiTooltip,
  Container,
  Divider,
  Avatar,
  Stack,
  Chip
} from "@mui/material";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";
import {
  InfoOutlined as InfoOutlinedIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AssessmentOutlined as AssessmentIcon,
  EventNote as EventNoteIcon
} from '@mui/icons-material';


import MarketingWidget from './MarketingWidget'; 
import {ENDPOINTS} from '../config';
import { t } from '../i18n/useTranslation';


const groupQuotesByMonth = (quotes) => {
  const monthlyCounts = quotes.reduce((acc, quote) => {
    const date = new Date(quote.createdAt);
    const monthYear = date.toLocaleString("default", { month: "short", year: "numeric" });

    if (!acc[monthYear]) {
      acc[monthYear] = { name: monthYear, quotes: 0, value: 0 };
    }
    acc[monthYear].quotes += 1;
    acc[monthYear].value += (quote.manualPrice || quote.totalPrice || 0);
    return acc;
  }, {});

  return Object.values(monthlyCounts).sort((a, b) => new Date(a.name) - new Date(b.name));
};

const calculateStats = (quotes) => {
  const totalQuotes = quotes.length;
  const totalPipelineValue = quotes.reduce((sum, q) => sum + (q.manualPrice || q.totalPrice || 0), 0);

  const successStatuses = ["SALE_AGREED", "INVOICED", "PAYMENT_RECEIVED", "COMPLETE", "APPROVED"];
  const wonQuotes = quotes.filter(q => successStatuses.includes(q.status)).length;
  const conversionRate = totalQuotes > 0 ? Math.round((wonQuotes / totalQuotes) * 100) : 0;

  const canopyCounts = quotes.reduce((acc, q) => {
    const name = q.canopy?.name || "Other";
    acc[name] = (acc[name] || 0) + 1;
    return acc;
  }, {});
  const popularCanopy = Object.keys(canopyCounts).reduce((a, b) => canopyCounts[a] > canopyCounts[b] ? a : b, "N/A");

  return { totalQuotes, totalPipelineValue, conversionRate, popularCanopy };
};

const getStatusData = (quotes) => {
  const counts = quotes.reduce((acc, q) => {
    const status = q.status || "PENDING";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});
  return Object.keys(counts).map(key => ({ name: key, value: counts[key] }));
};

const PIPELINE_STAGES = [
  { key: 'PENDING',               icon: '⏳', label: 'Pending',               desc: 'New quote, not yet acted on' },
  { key: 'CONTACTED',             icon: '📞', label: 'Contacted',             desc: 'Customer has been reached' },
  { key: 'LEFT_MESSAGE',          icon: '💬', label: 'Left Message',          desc: 'Awaiting customer callback' },
  { key: 'SURVEY_BOOKED',         icon: '📅', label: 'Survey Booked',         desc: 'Site visit scheduled' },
  { key: 'SURVEY_COMPLETED',      icon: '✏️', label: 'Survey Completed',      desc: 'Site assessed, ready to quote' },
  { key: 'REVISED_ESTIMATE_SENT', icon: '📤', label: 'Estimate Sent',         desc: 'Updated quote sent to customer' },
  { key: 'SALE_AGREED',           icon: '🤝', label: 'Sale Agreed',           desc: 'Customer has accepted the quote' },
  { key: 'INVOICED',              icon: '🧾', label: 'Invoiced',              desc: 'Invoice issued, awaiting payment' },
  { key: 'PAYMENT_RECEIVED',      icon: '💰', label: 'Payment Received',      desc: 'Payment confirmed' },
  { key: 'ORDERED',               icon: '📦', label: 'Ordered',               desc: 'Materials ordered from supplier' },
  { key: 'INSTALLED',             icon: '🔧', label: 'Installed',             desc: 'Installation complete' },
  { key: 'COMPLETE',              icon: '✅', label: 'Complete',              desc: 'Job fully finished and closed' },
  { key: 'REJECTED',              icon: '❌', label: 'Rejected',              desc: 'Deal did not proceed' },
];

// --- ENHANCED CUSTOM TOOLTIP COMPONENT ---
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Card elevation={0} sx={{ 
        minWidth: 140,
        background: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(12px)',
        border: '1px solid rgba(168, 133, 29, 0.2)', // Branded Gold border
        borderRadius: 2,
        boxShadow: '0 8px 32px rgba(168, 133, 29, 0.12)' // Premium Gold-tinted shadow
      }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#001E3C', mb: 0.5 }}>
            {label}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ 
              width: 8, 
              height: 8, 
              borderRadius: '50%', 
              bgcolor: '#a8851d' // Gold dot
            }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#001E3C' }}>
                {payload[0].value} {t('dashboard.totalQuotes').toLowerCase()}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }
  return null;
};

// --- KPI CARD COMPONENT ---
const KPICard = ({ title, value, icon, trend, loading, color = 'primary' }) => {
  const trendColor = trend && trend.value > 0 ? '#4CAF50' : '#F44336';
  const TrendIcon = trend && trend.value > 0 ? TrendingUpIcon : TrendingDownIcon;
  const getFontSize = (val) => {
    const len = String(val).length;
    if (len > 8) return '1.1rem';
    if (len > 6) return '1.5rem';
    return '2.25rem';
  };

  return (
    <Card 
      elevation={0}
      sx={{ 
        height: '100%',
        background: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: 3,
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 12px 24px rgba(0, 0, 0, 0.08)',
        }
      }}
    >
      <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Avatar sx={{ 
            bgcolor: 'rgba(168, 133, 29, 0.1)', // Gold glass-like background
            color: '#a8851d', // Branded Gold icon
            width: 56, 
            height: 56,
            mr: 2,
            border: '1px solid rgba(168, 133, 29, 0.15)'
          }}>
            {icon}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                color: 'text.secondary',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                fontSize: '0.875rem'
              }}
            >
              {title}
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, flexGrow: 1, flexWrap: 'wrap' }}>
          {loading ? (
            <CircularProgress size={40} sx={{ color: '#001E3C' }} />
          ) : (
            <>
              <Typography 
                sx={{ 
                  fontWeight: 700,
                  color: '#001E3C',
                  lineHeight: 1.1,
                  fontSize: getFontSize(value),
                  wordBreak: 'break-word',
                  minWidth: 0,
                  flex: 1,
                }}
              >
                {value}
              </Typography>
              {trend && (
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
                  <TrendIcon sx={{ color: trendColor, fontSize: 24, mr: 1 }} />
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      color: trendColor,
                      fontWeight: 600
                    }}
                  >
                    {Math.abs(trend.value)}%
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// --- MAIN DASHBOARD CHARTS COMPONENT ---
const DashboardCharts = ({ lang }) => {
  const [chartData, setChartData] = useState([]);
  const [stats, setStats] = useState({ totalQuotes: 0, thisMonthQuotes: 0 });
  const [allQuotes, setAllQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTimeframe, setSelectedTimeframe] = useState('All Time');

  const navigate = useNavigate();

  useEffect(() => {
    const fetchChartData = async () => {
      const cacheKey = 'alfresko_dashboard_all_quotes';

      try {
        const cachedData = sessionStorage.getItem(cacheKey);
        if (cachedData) {
          const parsed = JSON.parse(cachedData);
          setAllQuotes(parsed);
          setLoading(false);
        } else {
          setLoading(true);
        }

        const token = localStorage.getItem('authToken');

        if (!token) {
             console.error("No auth token found");
             navigate('/login');
             return;
        }
        const response = await fetch(`${ENDPOINTS.DEALER_PORTAL.MY_QUOTES}?page=1&pageSize=1000`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`, 
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          if (response.status === 401) {
            localStorage.removeItem('authToken');
            localStorage.removeItem('userRole');
            navigate('/login'); 
            return;
          }
          throw new Error(`API Error: ${response.status} ${response.statusText}`);
        }
        
        const responseJson = await response.json();
        const quotesData = Array.isArray(responseJson) 
          ? responseJson 
          : (responseJson.data || []);
        sessionStorage.setItem(cacheKey, JSON.stringify(quotesData));
        setAllQuotes(quotesData);

      } catch (error) {
        console.error("Error fetching chart data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, [navigate]);

  useEffect(() => {
    if (allQuotes.length > 0) {
      let filteredQuotes = allQuotes;
      if (selectedTimeframe !== 'All Time') {
        const days = parseInt(selectedTimeframe);
        const now = new Date();
        const startDate = new Date(now);
        startDate.setDate(now.getDate() - days);
        filteredQuotes = allQuotes.filter(q => new Date(q.createdAt) >= startDate);
      }
      
      const processedData = groupQuotesByMonth(filteredQuotes);
      const calculatedStats = calculateStats(filteredQuotes);
      
      setChartData(processedData);
      setStats(calculatedStats);
    }
  }, [allQuotes, selectedTimeframe]);

  const timeframeOptions = ['7D', '30D', '90D', 'All Time'];

  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        py: 6, 
        background: 'linear-gradient(to bottom, #f8f9fa, #ffffff)',
        minHeight: '100vh'
      }}
    >
      {/* Header Section */}
      <Box sx={{ mb: 6, textAlign: 'center' }}>
        <Typography 
          variant="h4" 
          sx={{ 
            color: '#001E3C',
            fontWeight: 600,
            mb: 2,
            letterSpacing: '-0.5px'
          }}
        >
          {t('dashboard.title')}
        </Typography>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            color: 'text.secondary',
            maxWidth: 600,
            mx: 'auto'
          }}
        >
          {t('dashboard.subtitle')}
        </Typography>
        
        {/* Date Range Controls */}
         <Paper 
          elevation={0}
          sx={{ 
            display: 'inline-flex', 
            mt: 4,
            p: 0.5,
            background: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(0, 30, 60, 0.08)',
            borderRadius: 2
          }}
        >
          <ButtonGroup 
            variant="outlined" 
            size="medium"
            sx={{
              '& .MuiButton-root': {
                borderColor: 'transparent',
                color: 'text.secondary',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                transition: 'all 0.2s ease'
              },
              '& .MuiButton-root.Mui-selected': {
                background: '#001E3C', // Deep Blue solid background on selection
                color: '#ffffff', // High-contrast white text
              },
              '& .MuiButton-root:hover': {
                background: 'rgba(0, 30, 60, 0.05)'
              }
            }}
          >
            {timeframeOptions.map((option) => (
              <Button 
                key={option}
                className={selectedTimeframe === option ? 'Mui-selected' : ''}
                onClick={() => setSelectedTimeframe(option)}
              >
                {option}
              </Button>
            ))}
          </ButtonGroup>
        </Paper>
      </Box>

      {/* --- INTEGRATION POINT: MARKETING WIDGET --- */}
      {/* Placed prominently between Controls and KPIs for maximum visibility */}
      <Box sx={{ mb: 6 }}>
        <MarketingWidget lang={lang}/>
      </Box>
      {/* ------------------------------------------- */}

      {/* 4-COLUMN EXECUTIVE KPI CARDS */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <KPICard
              title={t('dashboard.totalQuotes') || "Total Quotes"}
              value={stats.totalQuotes}
              icon={<AssessmentIcon sx={{ fontSize: 28 }} />}
              trend={{ value: 12.5 }}
              loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <KPICard
              title="Pipeline Value"
              value={`£${(stats.totalPipelineValue || 0).toLocaleString()}`}
              icon={<TrendingUpIcon sx={{ fontSize: 28 }} />}
              trend={{ value: 18.2 }}
              loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <KPICard
              title="Conversion Rate"
              value={`${stats.conversionRate || 0}%`}
              icon={<InfoOutlinedIcon sx={{ fontSize: 28 }} />}
              trend={{ value: 4.1 }}
              loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <KPICard
              title="Popular Canopy"
              value={(stats.popularCanopy || "N/A").replace(" Canopy", "")}
              icon={<EventNoteIcon sx={{ fontSize: 28 }} />}
              loading={loading}
          />
        </Grid>
      </Grid>

      {/* Chart Section */}
      <Grid container spacing={3}>
        
        {/* Left Side: Pipeline Growth AreaChart (70%) */}
        <Grid item xs={12} lg={8}>
          <Card elevation={0} sx={{ p: 3, border: '1px solid rgba(0,0,0,0.06)', borderRadius: 3, bgcolor: '#ffffff', height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#001E3C' }}>
                  Pipeline Growth Trend
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Monthly quote volume and monetary pipeline accumulation
                </Typography>
              </Box>
              <MuiTooltip title="Displays cumulative monthly metrics to track dealership velocity.">
                <InfoOutlinedIcon sx={{ color: 'text.disabled', cursor: 'help' }} />
              </MuiTooltip>
            </Box>
            
            <Divider sx={{ mb: 3 }} />
            
            <Box sx={{ height: 350, width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="areaGold" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#a8851d" stopOpacity={0.4}/>
                      <stop offset="95%" stopColor="#a8851d" stopOpacity={0.0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="name" tick={{ fill: '#666', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#666', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false}/>
                  <Tooltip content={<CustomTooltip />} />
                  <Area 
                    type="monotone" 
                    dataKey="quotes" 
                    stroke="#a8851d" 
                    strokeWidth={3} 
                    fillOpacity={1} 
                    fill="url(#areaGold)" 
                    dot={{ stroke: '#a8851d', strokeWidth: 2, r: 4, fill: '#fff' }}
                    activeDot={{ r: 6, strokeWidth: 0 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Card>
        </Grid>

        {/* Right Side: Deal Stages Donut Breakdown (30%) */}
        <Grid item xs={12} lg={4}>
          <Card elevation={0} sx={{ p: 3, border: '1px solid rgba(0,0,0,0.06)', borderRadius: 3, bgcolor: '#ffffff', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#001E3C', mb: 1 }}>
              Deal Stages
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Current distribution of quote states across pipeline
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Box sx={{ width: '100%' }}>
  <Typography variant="h3" sx={{ fontWeight: 800, color: '#001E3C', mb: 0.5, textAlign: 'center' }}>
    {stats.totalQuotes}
  </Typography>
  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, display: 'block', textAlign: 'center', mb: 3 }}>
    Total Active Deals
  </Typography>

  <Stack spacing={0.5}>
    {PIPELINE_STAGES.map((stage) => {
      const statusCounts = getStatusData(allQuotes);
      const match = statusCounts.find(s => s.name === stage.key);
      const count = match ? match.value : 0;
      const isActive = count > 0;

      return (
        <MuiTooltip key={stage.key} title={stage.desc} placement="left" arrow>
          <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: isActive ? 'rgba(168, 133, 29, 0.3)' : '#f0f0f0',
                    bgcolor: isActive ? 'rgba(168, 133, 29, 0.06)' : 'transparent',
                    opacity: isActive ? 1 : 0.4,
                    transition: 'all 0.2s ease',
                  }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span style={{ fontSize: '0.85rem' }}>{stage.emoji}</span>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: isActive ? 700 : 400, color: isActive ? '#001E3C' : 'text.disabled', fontSize: '0.78rem', lineHeight: 1.2 }}>
                          {stage.label}
                        </Typography>
                        {isActive && (
                          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem' }}>
                            {stage.desc}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                    {isActive && (
                      <Chip label={count} size="small" sx={{ fontWeight: 'bold', height: 20, fontSize: '0.72rem', bgcolor: '#a8851d', color: '#fff' }} />
                    )}
                  </Box>
                </MuiTooltip>
              );
            })}
          </Stack>
        </Box>
          </Card>
        </Grid>

      </Grid>
    </Container>
  );
};

export default DashboardCharts;